import React, { useMemo, useState } from 'react'
import { View } from 'react-native'
import { useTranslation } from 'react-i18next'
import {
  MelddAutocompleteInput,
  AutocompleteItem,
} from './meldd-autocomplete.input'
import { UserLink } from '../typo/user.link'
import { createStyle } from '../../contexts/global-styles.provider'
import {
  CollaboratorsQuery,
  useCollaboratorsQuery,
} from '../../generated/graphql'

type CollaboratorUser = NonNullable<
  NonNullable<CollaboratorsQuery['collaborators']>['users']
>[number]

interface CollaboratorAutocompleteInputProps {
  taskId: string
  onSelect: (collaborator: CollaboratorUser) => void
  placeholder?: string
  limit?: number
  loading?: boolean
}

export const CollaboratorAutocompleteInput: React.FC<
  CollaboratorAutocompleteInputProps
> = ({
  taskId,
  onSelect,
  placeholder,
  limit = 10,
  loading: externalLoading,
}) => {
  const { t } = useTranslation(['tasks', 'nodes'])
  const [search, setSearch] = useState('')
  const styles = useStyle()

  const { data, loading: queryLoading } = useCollaboratorsQuery({
    variables: {
      pagination: {
        offset: 0,
        limit,
        sortBy: 'username',
        sort: 'ASC',
      },
      search: search || undefined,
    },
    skip: !search || search.length < 2,
  })

  const collaborators = data?.collaborators.users || []
  const loading = queryLoading || externalLoading || false

  const autocompleteItems: AutocompleteItem[] = useMemo(
    () =>
      collaborators.map((collaborator) => {
        return {
          id: collaborator.id,
          label: collaborator.username || '',
          renderItem: () => (
            <View style={styles.suggestionContent}>
              <UserLink user={collaborator} />
            </View>
          ),
        }
      }),
    [collaborators, styles]
  )

  const handleSelect = (item: AutocompleteItem) => {
    const collaborator = collaborators.find((c) => c.id === item.id)
    if (collaborator) {
      onSelect(collaborator)
      setSearch('')
    }
  }

  return (
    <MelddAutocompleteInput
      placeholder={
        placeholder ||
        t('tasks:search-collaborators') ||
        t('nodes:search-collaborators')
      }
      value={search}
      onChangeText={setSearch}
      items={autocompleteItems}
      onSelect={handleSelect}
      loading={loading}
      minSearchLength={2}
      emptyMessage={t('tasks:no-users-found') || t('nodes:no-users-found')}
    />
  )
}

const useStyle = createStyle(({ theme }) => ({
  suggestionContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
}))
